import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Nav from '../components/Nav';
import { isOrgAdmin } from '../auth/cognito';
import { getTeamMembers, inviteTeamMember, removeTeamMember, updateTeamMemberRole, resendInvite, deleteInvite, TeamMember } from '../api/team';

export default function Team() {
  const navigate = useNavigate();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('user');
  const [inviteStatus, setInviteStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [updatingRoleId, setUpdatingRoleId] = useState<string | null>(null);
  const [pendingStatuses, setPendingStatuses] = useState<Record<string, boolean>>({}); // userId -> accepted
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [deletingInviteId, setDeletingInviteId] = useState<string | null>(null);
  const [resendMsg, setResendMsg] = useState<{ userId: string; msg: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!isOrgAdmin()) {
      navigate('/');
      return;
    }
    load();
  }, []);

  async function load() {
    setLoading(true);
    const data = await getTeamMembers();
    setMembers(data);
    setLoading(false);
    // Fetch Cognito status for all active members in parallel
    const active = data.filter(m => m.status === 'active');
    const statuses = await Promise.all(active.map(m =>
      fetch(`https://0emko5cw6f.execute-api.ca-central-1.amazonaws.com/prod/org/users/${m.userId}/status`, {
        headers: { 'Authorization': localStorage.getItem('pe_id_token') || '', 'Content-Type': 'application/json' }
      }).then(r => r.json()).then(d => ({ userId: m.userId, accepted: d.accepted })).catch(() => ({ userId: m.userId, accepted: true }))
    ));
    const statusMap: Record<string, boolean> = {};
    statuses.forEach(s => { statusMap[s.userId] = s.accepted; });
    setPendingStatuses(statusMap);
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setInviteLoading(true);
    setInviteStatus(null);
    const res = await inviteTeamMember(email, firstName, lastName, role);
    setInviteLoading(false);
    if (res.success) {
      setInviteStatus({ type: 'success', msg: res.message || `Invite sent to ${email}` });
      setEmail(''); setFirstName(''); setLastName(''); setRole('user');
      load();
    } else {
      setInviteStatus({ type: 'error', msg: res.error || 'Something went wrong.' });
    }
  }

  async function handleRoleChange(member: TeamMember, newRole: string) {
    if (newRole === member.role) return;
    const label = newRole === 'org_admin' ? 'Admin' : 'Member';
    if (!window.confirm(`Change ${member.email} to ${label}? This updates their access immediately.`)) return;
    setUpdatingRoleId(member.userId);
    const res = await updateTeamMemberRole(member.userId, newRole);
    setUpdatingRoleId(null);
    if (res.success) {
      setMembers(prev => prev.map(m => m.userId === member.userId ? { ...m, role: newRole } : m));
    } else {
      alert(res.error || 'Failed to update role.');
    }
  }

  async function handleResend(member: TeamMember) {
    setResendingId(member.userId);
    setResendMsg(null);
    const res = await resendInvite(member.userId);
    setResendingId(null);
    setResendMsg({ userId: member.userId, msg: res.message || res.error || '', type: res.success ? 'success' : 'error' });
    setTimeout(() => setResendMsg(null), 4000);
  }

  async function handleDeleteInvite(member: TeamMember) {
    if (!window.confirm(`Delete invite for ${member.email}? This will remove their Cognito account and free the seat.`)) return;
    setDeletingInviteId(member.userId);
    const res = await deleteInvite(member.userId);
    setDeletingInviteId(null);
    if (res.success) {
      setMembers(prev => prev.filter(m => m.userId !== member.userId));
    } else {
      alert(res.error || 'Failed to delete invite.');
    }
  }

  async function handleRemove(userId: string) {
    if (!window.confirm('Remove this team member? They will lose access immediately.')) return;
    setRemovingId(userId);
    const res = await removeTeamMember(userId);
    setRemovingId(null);
    if (res.success) {
      setMembers(prev => prev.filter(m => m.userId !== userId));
    } else {
      alert(res.error || 'Failed to remove user.');
    }
  }

  const active = members.filter(m => m.status === 'active');
  const accepted = active.filter(m => pendingStatuses[m.userId] !== false);
  const pending = active.filter(m => pendingStatuses[m.userId] === false);
  const suspended = members.filter(m => m.status === 'suspended');

  function alertStyle(type: 'success' | 'error'): React.CSSProperties {
    return {
      padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 16,
      background: type === 'success' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
      border: `1px solid ${type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
      color: type === 'success' ? '#4ade80' : '#f87171',
    };
  }

  const s: Record<string, React.CSSProperties> = {
    page: { minHeight: '100vh', background: 'linear-gradient(135deg,#0a0a1a,#0d1b3e,#050d1f)', fontFamily: "'Segoe UI',system-ui,sans-serif", color: '#fff', paddingTop: 80 },
    container: { maxWidth: 900, margin: '0 auto', padding: '32px 16px' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, flexWrap: 'wrap' as const, gap: 16 },
    title: { fontSize: 'clamp(22px,4vw,30px)', fontWeight: 700, letterSpacing: -0.5 },
    sub: { fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 4 },
    inviteBtn: { background: 'linear-gradient(135deg,#6366f1,#4f46e5)', border: 'none', color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
    card: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 16, padding: 24, marginBottom: 24 },
    sectionLabel: { fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 2, color: '#818cf8', fontWeight: 700, marginBottom: 16 },
    row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', flexWrap: 'wrap' as const, gap: 10 },
    divider: { borderTop: '1px solid rgba(255,255,255,0.06)' },
    name: { fontSize: 14, fontWeight: 600, color: '#fff' },
    email: { fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 },
    badge: { fontSize: 11, padding: '3px 10px', borderRadius: 100, fontWeight: 700 },
    removeBtn: { background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
    resendBtn: { background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.3)', color: '#a5b4fc', padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
    formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 },
    input: { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', padding: '10px 14px', borderRadius: 10, fontSize: 13, width: '100%', boxSizing: 'border-box' as const },
    label: { fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 6, display: 'block' },
    select: { background: '#1a1f3c', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', padding: '10px 14px', borderRadius: 10, fontSize: 13, width: '100%', boxSizing: 'border-box' as const },
    submitBtn: { background: 'linear-gradient(135deg,#6366f1,#4f46e5)', border: 'none', color: '#fff', padding: '11px 24px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', marginTop: 4 },
    cancelBtn: { background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.5)', padding: '11px 20px', borderRadius: 10, fontSize: 13, cursor: 'pointer', marginTop: 4, marginLeft: 10 },
  };

  function roleBadge(r: string) {
    if (r === 'org_admin') return <span style={{ ...s.badge, background: 'rgba(168,85,247,0.15)', color: '#c084fc', border: '1px solid rgba(168,85,247,0.3)' }}>Admin</span>;
    return <span style={{ ...s.badge, background: 'rgba(99,102,241,0.12)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.25)' }}>Member</span>;
  }

  return (
    <div style={s.page}>
      <Nav />
      <div style={s.container}>
        <div style={s.header}>
          <div>
            <div style={s.title}>Team Management</div>
            <div style={s.sub}>Invite and manage your organisation's members</div>
          </div>
          <button style={s.inviteBtn} onClick={() => { setShowInvite(v => !v); setInviteStatus(null); }}>
            {showInvite ? 'Cancel' : '+ Invite Member'}
          </button>
        </div>

        {showInvite && (
          <div style={s.card}>
            <div style={s.sectionLabel}>Invite a New Member</div>
            {inviteStatus && <div style={alertStyle(inviteStatus.type)}>{inviteStatus.msg}</div>}
            <form onSubmit={handleInvite}>
              <div style={s.formGrid}>
                <div>
                  <label style={s.label}>First Name</label>
                  <input style={s.input} placeholder="Jane" value={firstName} onChange={e => setFirstName(e.target.value)} />
                </div>
                <div>
                  <label style={s.label}>Last Name</label>
                  <input style={s.input} placeholder="Smith" value={lastName} onChange={e => setLastName(e.target.value)} />
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={s.label}>Email Address *</label>
                <input style={s.input} type="email" placeholder="jane@company.com" value={email} onChange={e => setEmail(e.target.value)} required />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={s.label}>Role</label>
                <select style={s.select} value={role} onChange={e => setRole(e.target.value)}>
                  <option value="user">Member</option>
                  <option value="org_admin">Admin</option>
                </select>
              </div>
              <div>
                <button style={s.submitBtn} type="submit" disabled={inviteLoading}>
                  {inviteLoading ? 'Sending...' : 'Send Invite'}
                </button>
                <button style={s.cancelBtn} type="button" onClick={() => setShowInvite(false)}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {!loading && pending.length > 0 && (
          <div style={{ ...s.card, borderColor: 'rgba(251,191,36,0.2)' }}>
            <div style={{ ...s.sectionLabel, color: '#fbbf24' }}>Pending Invites ({pending.length})</div>
            {pending.map((m, i) => (
              <div key={m.userId} style={{ ...s.row, ...(i > 0 ? s.divider : {}) }}>
                <div>
                  <div style={s.name}>{[m.firstName, m.lastName].filter(Boolean).join(' ') || m.email}</div>
                  <div style={s.email}>{m.email}</div>
                  <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 4 }}>
                    Invite sent {new Date(m.invitedAt).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })} · awaiting acceptance
                  </div>
                  {resendMsg?.userId === m.userId && (
                    <div style={{ fontSize: 12, marginTop: 6, color: resendMsg.type === 'success' ? '#4ade80' : '#f87171' }}>
                      {resendMsg.msg}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ ...s.badge, background: 'rgba(251,191,36,0.1)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.25)' }}>Pending</span>
                  <button
                    style={{ ...s.resendBtn, opacity: resendingId === m.userId ? 0.5 : 1 }}
                    disabled={resendingId === m.userId}
                    onClick={() => handleResend(m)}
                  >
                    {resendingId === m.userId ? '...' : 'Resend'}
                  </button>
                  <button
                    style={{ ...s.removeBtn, opacity: deletingInviteId === m.userId ? 0.5 : 1 }}
                    disabled={deletingInviteId === m.userId}
                    onClick={() => handleDeleteInvite(m)}
                  >
                    {deletingInviteId === m.userId ? '...' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={s.card}>
          <div style={s.sectionLabel}>Active Members ({loading ? '…' : accepted.length})</div>
          {loading ? (
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>Loading...</div>
          ) : accepted.length === 0 ? (
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>No active members yet. Invite your first team member above.</div>
          ) : (
            accepted.map((m, i) => (
              <div key={m.userId} style={{ ...s.row, ...(i > 0 ? s.divider : {}) }}>
                <div>
                  <div style={s.name}>{[m.firstName, m.lastName].filter(Boolean).join(' ') || m.email}</div>
                  <div style={s.email}>{m.email}</div>
                  {m.lastActive && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', marginTop: 3 }}>Last active {new Date(m.lastActive).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}</div>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <select
                    value={m.role}
                    disabled={updatingRoleId === m.userId}
                    onChange={e => handleRoleChange(m, e.target.value)}
                    style={{ background: '#1a1f3c', border: '1px solid rgba(255,255,255,0.12)', color: m.role === 'org_admin' ? '#c084fc' : '#a5b4fc', padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: updatingRoleId === m.userId ? 0.5 : 1 }}
                  >
                    <option value="user">Member</option>
                    <option value="org_admin">Admin</option>
                  </select>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>
                    Joined {new Date(m.invitedAt).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                  <button
                    style={{ ...s.removeBtn, opacity: removingId === m.userId ? 0.5 : 1 }}
                    disabled={removingId === m.userId}
                    onClick={() => handleRemove(m.userId)}
                  >
                    {removingId === m.userId ? '...' : 'Remove'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {!loading && suspended.length > 0 && (
          <div style={{ ...s.card, opacity: 0.6 }}>
            <div style={s.sectionLabel}>Suspended ({suspended.length})</div>
            {suspended.map((m, i) => (
              <div key={m.userId} style={{ ...s.row, ...(i > 0 ? s.divider : {}) }}>
                <div>
                  <div style={{ ...s.name, color: 'rgba(255,255,255,0.4)' }}>{[m.firstName, m.lastName].filter(Boolean).join(' ') || m.email}</div>
                  <div style={s.email}>{m.email}</div>
                </div>
                <span style={{ ...s.badge, background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)' }}>Suspended</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
